import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { useAuth } from "../context/AuthContext";import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { BrandMark, BRAND } from "../components/Brand";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from "../components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { ArrowLeft, Plus, PencilSimple, Trash, User as UserIcon, FolderOpen } from "@phosphor-icons/react";
import { ROLE_BADGE_CLASS, ROLE_LABEL } from "../lib/permissions";

const ROLES = ["admin", "editor", "viewer"];

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "viewer", project_ids: [] });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [u, p] = await Promise.all([api.get("/users"), api.get("/projects")]);
      setUsers(u.data.users || []);
      setProjects(p.data.projects || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ email: "", password: "", name: "", role: "viewer", project_ids: [] });
    setFormOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({ email: u.email, password: "", name: u.name || "", role: u.role, project_ids: u.project_ids || [] });
    setFormOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        const patch = { name: form.name, role: form.role, project_ids: form.project_ids };
        if (form.password) patch.password = form.password;
        await api.patch(`/users/${editing.id}`, patch);
        toast.success("User updated");
      } else {
        await api.post("/users", form);
        toast.success("User created");
      }
      setFormOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/users/${deleting.id}`);
      toast.success("User deleted");
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col" data-testid="users-page">
      <header className="h-16 border-b border-[#E5E5E5] bg-white flex items-center px-6 justify-between shrink-0">
        <div className="flex items-center gap-3">
          <BrandMark />
          <div className="flex flex-col leading-tight">
            <span className="text-sm font-black tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>
              PROJECT TRACKER
            </span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
              User Management
            </span>
          </div>
        </div>
        <Link to="/">
          <Button variant="outline" size="sm" data-testid="back-to-dashboard-btn" className="h-9">
            <ArrowLeft size={16} weight="bold" className="mr-2" />
            Back to Tracker
          </Button>
        </Link>
      </header>

      <div className="flex-1 p-6 max-w-5xl w-full mx-auto">
        <div className="flex items-end justify-between mb-6">
          <div>
            <p
              className="text-[10px] uppercase tracking-[0.3em] text-neutral-500"
              style={{ fontFamily: "IBM Plex Mono, monospace" }}
            >
              Access Control
            </p>
            <h1
              className="text-3xl font-black tracking-tight mt-1"
              style={{ fontFamily: "Chivo, sans-serif" }}
            >
              Users & Roles
            </h1>
            <p className="text-sm text-neutral-500 mt-1">
              Admins can add and manage teammates. Editors can create/update tasks. Viewers are read-only.
            </p>
          </div>
          <Button
            onClick={openCreate}
            data-testid="new-user-btn"
            className="bg-[#387EE6] hover:bg-[#2563EB] text-white"
          >
            <Plus size={16} weight="bold" className="mr-2" />
            New User
          </Button>
        </div>

        <div className="bg-white border border-[#E5E5E5] rounded-lg overflow-hidden">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-[#F5F5F5] border-b border-[#E5E5E5]">
                {["User", "Email", "Role", "Created", "Actions"].map((h) => (
                  <th key={h} className="px-4 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-neutral-600">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-neutral-500">Loading…</td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-neutral-500">No users yet.</td></tr>
              ) : users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-[#E5E5E5] hover:bg-[#FAFAFA]">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-neutral-200 flex items-center justify-center">
                        <UserIcon size={14} weight="bold" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">{u.name || u.email.split("@")[0]}</span>
                        {u.id === me?.id ? (
                          <span className="text-[10px] uppercase tracking-wider text-neutral-400">You</span>
                        ) : null}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-neutral-700 font-mono">{u.email}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${ROLE_BADGE_CLASS[u.role]}`}
                      data-testid={`user-role-${u.id}`}
                    >
                      {ROLE_LABEL[u.role] || u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-neutral-500">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => openEdit(u)}
                        data-testid={`edit-user-btn-${u.id}`}
                        className="h-7 w-7 flex items-center justify-center rounded hover:bg-neutral-200 text-neutral-700"
                      >
                        <PencilSimple size={14} weight="bold" />
                      </button>
                      <button
                        onClick={() => setDeleting(u)}
                        disabled={u.id === me?.id}
                        data-testid={`delete-user-btn-${u.id}`}
                        className="h-7 w-7 flex items-center justify-center rounded hover:bg-red-50 text-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
                        title={u.id === me?.id ? "Cannot delete yourself" : "Delete user"}
                      >
                        <Trash size={14} weight="bold" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Sheet open={formOpen} onOpenChange={setFormOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md p-0 bg-white flex flex-col h-full" data-testid="user-form-sheet">
          <form onSubmit={submit} className="flex flex-col h-full">
            <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#E5E5E5] shrink-0">
              <SheetTitle className="text-2xl font-black tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>
                {editing ? "Edit User" : "New User"}
              </SheetTitle>
              <SheetDescription className="text-neutral-500">
                {editing ? "Update the user's role or reset their password." : "Add a teammate with the appropriate role."}
              </SheetDescription>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Email</Label>
                <Input
                  data-testid="user-form-email"
                  type="email"
                  required
                  disabled={!!editing}
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Name</Label>
                <Input
                  data-testid="user-form-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-form-role" className="h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => (
                      <SelectItem key={r} value={r}>{ROLE_LABEL[r]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-neutral-500">
                  {form.role === "admin" && "Full access — user management + delete + import."}
                  {form.role === "editor" && "Create & update tasks. Cannot delete or import."}
                  {form.role === "viewer" && "Read-only. Can export."}
                </p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">
                  {editing ? "New Password (leave blank to keep)" : "Password"}
                </Label>
                <Input
                  data-testid="user-form-password"
                  type="password"
                  required={!editing}
                  minLength={6}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                />
              </div>

              {form.role !== "admin" ? (
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">
                    Project Access
                  </Label>
                  <div className="max-h-40 overflow-y-auto border border-neutral-200 rounded-md p-2 space-y-1" data-testid="user-form-projects">
                    {projects.length === 0 ? (
                      <p className="text-xs text-neutral-500 italic px-1">No projects available. Add one in Setup.</p>
                    ) : projects.map((p) => (
                      <label key={p.id} className="flex items-center gap-2 py-1 px-1 hover:bg-neutral-50 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={form.project_ids.includes(p.id)}
                          onChange={() => {
                            const has = form.project_ids.includes(p.id);
                            setForm({
                              ...form,
                              project_ids: has
                                ? form.project_ids.filter((x) => x !== p.id)
                                : [...form.project_ids, p.id],
                            });
                          }}
                          className="cursor-pointer accent-neutral-900"
                          data-testid={`user-form-project-${p.id}`}
                        />
                        <FolderOpen size={12} weight="bold" className="text-sky-700" />
                        <span className="text-sm text-neutral-900">{p.name}</span>
                        {p.code ? <span className="text-[10px] font-mono bg-neutral-100 px-1 py-0.5 rounded">{p.code}</span> : null}
                      </label>
                    ))}
                  </div>
                  <p className="text-[10px] text-neutral-500">Users only see tasks in projects they are members of.</p>
                </div>
              ) : (
                <p className="text-xs text-neutral-500 italic">Admins automatically have access to every project.</p>
              )}
            </div>
            <SheetFooter className="px-6 py-4 border-t border-[#E5E5E5] flex flex-row justify-end gap-2 shrink-0">
              <Button type="button" variant="outline" onClick={() => setFormOpen(false)} data-testid="user-form-cancel-btn">
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="user-form-submit-btn" className="bg-[#387EE6] hover:bg-[#2563EB] text-white">
                {saving ? "Saving..." : editing ? "Save" : "Create"}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent data-testid="delete-user-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this user?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove <span className="font-semibold">{deleting?.email}</span>. They will no longer be able to sign in.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-user-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="delete-user-confirm-btn" className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
